import * as Sentry from "@sentry/react";
import {
  CarbonMeasurementSystem,
  MeasurementSystem,
} from "portal/utils/units/units";
import { CarbonUser } from "portal/utils/auth";
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { CustomerResponse } from "protos/portal/customers";
import { FleetView, UserResponse } from "protos/portal/users";
import { StreamToken } from "portal/utils/stream";
import { toImplementationStatus } from "portal/utils/robots";
import { values } from "common/utils/objects";

interface State {
  user?: CarbonUser;
  customer?: CustomerResponse;
  permissions: Record<string, true>;
  isInternal: boolean;
  isLoading: boolean;
  measurementSystem: CarbonMeasurementSystem;
  settings: CarbonUser["userMetadata"];
  streamToken?: StreamToken;
  fleetViews: Record<number, FleetView>;
}

const initialState: State = {
  permissions: {},
  isInternal: false,
  isLoading: true,
  measurementSystem: MeasurementSystem.imperial,
  settings: {},
  fleetViews: {},
};

const isCarbonMeasurementSystem = (
  input: string | undefined
): input is CarbonMeasurementSystem =>
  values(CarbonMeasurementSystem).includes(input as CarbonMeasurementSystem);

export const self = createSlice({
  name: "self",
  initialState,
  reducers: {
    setStreamToken: (state, action: PayloadAction<StreamToken>) => {
      state.streamToken = action.payload;
    },
    setSelf: (state, action: PayloadAction<UserResponse>) => {
      state.user = action.payload.user;
      state.customer = action.payload.customer;
      state.permissions = Object.fromEntries(
        action.payload.permissions?.names.map((key) => [key, true]) ?? []
      );
      state.fleetViews = {};
      for (const view of action.payload.fleetViews) {
        if (!view.db) {
          continue;
        }
        state.fleetViews[view.db.id] = {
          ...view,
          statuses: view.statuses.map((status) =>
            toImplementationStatus(status)
          ),
        };
      }
      state.isInternal = state.user?.isInternal ?? false;
      state.measurementSystem = isCarbonMeasurementSystem(
        state.user?.userMetadata?.unit
      )
        ? state.user.userMetadata.unit
        : MeasurementSystem.imperial;
      state.settings = state.user?.userMetadata ?? {};
      state.isLoading = false;
      Sentry.setUser(
        state.user
          ? {
              email: state.user.email,
              segment: state.isInternal ? "carbon" : "customer",
            }
          : // null required by Sentry
            // eslint-disable-next-line unicorn/no-null
            null
      );
    },
    setFleetView: (state, action: PayloadAction<FleetView>) => {
      if (!action.payload.db) {
        return;
      }
      state.fleetViews[action.payload.db.id] = action.payload;
    },
    unsetFleetView: (state, action: PayloadAction<number>) => {
      delete state.fleetViews[action.payload];
    },
  },
});

export const { setSelf, setStreamToken, setFleetView, unsetFleetView } =
  self.actions;
